import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, IconButton, useTheme } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const DAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const PRESETS = [
    { label: 'Today', getValue: () => { const d = new Date(); return [d, d]; } },
    { label: 'Yesterday', getValue: () => { const d = new Date(); d.setDate(d.getDate() - 1); return [d, d]; } },
    { label: 'Last week', getValue: () => { const e = new Date(); const s = new Date(); s.setDate(e.getDate() - 7); return [s, e]; } },
    { label: 'Last month', getValue: () => { const e = new Date(); const s = new Date(); s.setMonth(e.getMonth() - 1); return [s, e]; } },
    { label: 'Last quarter', getValue: () => { const e = new Date(); const s = new Date(); s.setMonth(e.getMonth() - 3); return [s, e]; } },
];

function getDaysInMonth(year, month) {
    return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
    const d = new Date(year, month, 1).getDay();
    return d === 0 ? 6 : d - 1; // Mon=0
}

function isSameDay(a, b) {
    if (!a || !b) return false;
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function isInRange(day, from, to) {
    if (!from || !to) return false;
    return day >= from && day <= to;
}

function formatShort(d) {
    if (!d) return '';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
}

export default function DateRangePicker({ fromDate, toDate, onChange }) {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';

    const [open, setOpen] = useState(false);
    const [viewYear, setViewYear] = useState(new Date().getFullYear());
    const [viewMonth, setViewMonth] = useState(new Date().getMonth());
    const [selFrom, setSelFrom] = useState(fromDate ? new Date(fromDate) : null);
    const [selTo, setSelTo] = useState(toDate ? new Date(toDate) : null);
    const [picking, setPicking] = useState('from'); // 'from' or 'to'
    const ref = useRef(null);

    useEffect(() => {
        const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    const daysInMonth = getDaysInMonth(viewYear, viewMonth);
    const firstDay = getFirstDayOfMonth(viewYear, viewMonth);
    const monthName = new Date(viewYear, viewMonth).toLocaleString('en', { month: 'long' });

    const prevMonth = () => { if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1); } else setViewMonth(viewMonth - 1); };
    const nextMonth = () => { if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1); } else setViewMonth(viewMonth + 1); };

    const handleDayClick = (day) => {
        const clicked = new Date(viewYear, viewMonth, day);
        if (picking === 'from') {
            setSelFrom(clicked);
            setSelTo(null);
            setPicking('to');
            onChange && onChange(clicked, null);
            return;
        }

        
        let newFrom = selFrom;
        let newTo = clicked;
        if (!selFrom || clicked < selFrom) {
            newFrom = clicked;
            newTo = selFrom || clicked;
        }
        setSelFrom(newFrom);
        setSelTo(newTo);
        setPicking('from');
        onChange && onChange(newFrom, newTo);
    };

    const handlePreset = (preset) => {
        const [s, e] = preset.getValue();
        setSelFrom(s); setSelTo(e);
        setViewYear(s.getFullYear()); setViewMonth(s.getMonth());
        onChange && onChange(s, e);
    };

    const handleReset = () => {
        setSelFrom(null); setSelTo(null); setPicking('from');
        onChange && onChange(null, null);
    };

    const displayLabel = (selFrom && selTo)
        ? `${formatShort(selFrom)} – ${formatShort(selTo)}`
        : (selFrom ? `${formatShort(selFrom)} – ...` : 'Select dates');

    // Build calendar grid
    const prevMonthDays = getDaysInMonth(viewYear, viewMonth - 1);
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push({ day: prevMonthDays - firstDay + 1 + i, current: false });
    for (let i = 1; i <= daysInMonth; i++) cells.push({ day: i, current: true });
    const remaining = 42 - cells.length;
    for (let i = 1; i <= remaining; i++) cells.push({ day: i, current: false });

    return (
        <Box ref={ref} sx={{ position: 'relative', display: 'inline-flex' }}>
            {/* Trigger */}
            <Box onClick={() => setOpen(!open)} sx={{
                display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer',
                backgroundColor: isDarkMode ? '#111' : '#fff',
                border: isDarkMode ? '1px solid #333' : '1px solid #ccc',
                borderRadius: '6px', px: 2, py: 0.8,
                '&:hover': { borderColor: isDarkMode ? '#555' : '#888' }
            }}>
                <Typography variant="caption" sx={{ color: isDarkMode ? '#888' : '#666', whiteSpace: 'nowrap' }}>Date Range:</Typography>
                <Typography variant="caption" sx={{ color: isDarkMode ? '#fff' : '#000', whiteSpace: 'nowrap' }}>{displayLabel}</Typography>
                <ChevronLeftIcon sx={{ fontSize: 14, color: isDarkMode ? '#666' : '#999', transform: 'rotate(-90deg)' }} />
            </Box>

            {/* Dropdown */}
            {open && (
                <Box sx={{
                    position: 'absolute', top: '100%', right: 0, mt: 1, zIndex: 9999,
                    display: 'flex',
                    backgroundColor: isDarkMode ? '#151520' : '#fff',
                    border: isDarkMode ? '1px solid #333' : '1px solid #e0e0e0',
                    borderRadius: '12px', overflow: 'hidden',
                    boxShadow: isDarkMode ? '0 8px 32px rgba(0,0,0,0.5)' : '0 8px 32px rgba(0,0,0,0.1)',
                }}>
                    {/* Presets */}
                    <Box sx={{ p: 2.5, pr: 2, borderRight: isDarkMode ? '1px solid #222' : '1px solid #e0e0e0', minWidth: '120px', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {PRESETS.map(p => (
                            <Typography key={p.label} variant="caption" onClick={() => handlePreset(p)}
                                sx={{ color: isDarkMode ? '#aaa' : '#555', cursor: 'pointer', '&:hover': { color: isDarkMode ? '#fff' : '#000' }, fontSize: '0.8rem' }}>
                                {p.label}
                            </Typography>
                        ))}
                        <Box sx={{ mt: 'auto', pt: 2 }}>
                            <Typography variant="caption" onClick={handleReset}
                                sx={{ color: '#3c50b4', cursor: 'pointer', '&:hover': { color: '#7986cb' }, fontSize: '0.8rem' }}>
                                Reset
                            </Typography>
                        </Box>
                    </Box>

                    {/* Calendar */}
                    <Box sx={{ p: 2.5, minWidth: '260px' }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                            <Typography variant="body2" sx={{ color: isDarkMode ? '#fff' : '#000', fontWeight: 'bold' }}>{monthName} {viewYear}</Typography>
                            <Box>
                                <IconButton size="small" onClick={prevMonth} sx={{ color: isDarkMode ? '#888' : '#666', '&:hover': { color: isDarkMode ? '#fff' : '#000' } }}>
                                    <ChevronLeftIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" onClick={nextMonth} sx={{ color: isDarkMode ? '#888' : '#666', '&:hover': { color: isDarkMode ? '#fff' : '#000' } }}>
                                    <ChevronRightIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        </Box>
                        {/* Day headers */}
                        <Box display="grid" gridTemplateColumns="repeat(7, 1fr)" gap={0} mb={0.5}>
                            {DAYS.map(d => (
                                <Box key={d} sx={{ textAlign: 'center', py: 0.5 }}>
                                    <Typography variant="caption" sx={{ color: isDarkMode ? '#666' : '#888', fontSize: '0.7rem' }}>{d}</Typography>
                                </Box>
                            ))}
                        </Box>
                        {/* Days */}
                        <Box display="grid" gridTemplateColumns="repeat(7, 1fr)" gap={0}>
                            {cells.map((cell, i) => {
                                const cellDate = cell.current ? new Date(viewYear, viewMonth, cell.day) : null;
                                const isFrom = cell.current && isSameDay(cellDate, selFrom);
                                const isTo = cell.current && isSameDay(cellDate, selTo);
                                const inRange = cell.current && isInRange(cellDate, selFrom, selTo);
                                const isSelected = isFrom || isTo;

                                return (
                                    <Box key={i}
                                        onClick={() => cell.current && handleDayClick(cell.day)}
                                        sx={{
                                            textAlign: 'center', py: 0.6, cursor: cell.current ? 'pointer' : 'default',
                                            borderRadius: isSelected ? '50%' : 0,
                                            backgroundColor: isSelected ? '#3c50b4' : (inRange ? (isDarkMode ? 'rgba(92,107,192,0.2)' : 'rgba(92,107,192,0.1)') : 'transparent'),
                                            '&:hover': cell.current ? { backgroundColor: isSelected ? '#4f5b93' : (isDarkMode ? 'rgba(92,107,192,0.15)' : 'rgba(92,107,192,0.05)'), borderRadius: '50%' } : {},
                                        }}
                                    >
                                        <Typography variant="caption" sx={{
                                            color: !cell.current ? (isDarkMode ? '#444' : '#ccc') : (isSelected ? '#fff' : (inRange ? (isDarkMode ? '#a5b0e0' : '#3c50b4') : (isDarkMode ? '#ccc' : '#333'))),
                                            fontSize: '0.78rem', fontWeight: isSelected ? 'bold' : 'normal',
                                        }}>
                                            {cell.day}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                </Box>
            )}
        </Box>
    );
}
